import React from 'react';
import PropTypes from 'prop-types';
import Markdown from 'markdown-to-jsx';

const { node, oneOf, string } = PropTypes;

/** A link's text without the anchor — a tile must not nest interactive content in its button. */
const PlainText = ({ children = null }) => <>{children}</>;

PlainText.propTypes = { children: node };

const STRONG = { props: { className: 'sui-font-bold sui-font-normal' } };

// eslint-disable-next-line max-len
const BODY_TEXT = 'sui-text-base sui-leading-normal sui-font-regular sui-font-normal sui-tracking-normal sui-normal-case sui-text-primary';
// eslint-disable-next-line max-len
const TILE_TEXT = 'sui-text-base sui-leading-tight sui-font-regular sui-font-normal sui-tracking-normal sui-normal-case sui-text-primary';

const VARIANTS = {
  body: {
    className: BODY_TEXT,
    spaceParagraphs: true,
    overrides: {
      // eslint-disable-next-line id-length
      a: {
        component: 'a',
        props: {
          className: 'sui-underline sui-break-normal'
        }
      },
      ol: {
        props: { className: 'sui-list-decimal' }
      },
      ul: {
        props: { className: 'sui-list-disc' }
      },
      li: {
        props: { className: 'sui-mb-1 sui-ml-5 sui-list-outside' }
      },
      strong: STRONG
    }
  },
  tile: {
    className: TILE_TEXT,
    spaceParagraphs: false,
    overrides: {
      // eslint-disable-next-line id-length
      a: { component: PlainText },
      strong: STRONG,
    },
  },
  // Renders inside `Pill`'s label span, so it wraps in a span rather than a div and inherits Pill's
  // own type rather than setting a size of its own.
  pill: {
    wrapper: 'span',
    forceInline: true,
    spaceParagraphs: false,
    overrides: {
      // eslint-disable-next-line id-length
      a: { component: PlainText },
      strong: STRONG,
    },
  },
};

export const MagicMarkdown = ({ children = '', variant = 'body' }) => {
  if (!children) {
    return null;
  }

  // Fall back rather than destructure undefined — an unrecognised variant should render plainly
  // instead of throwing and taking the banner down.
  const {
    className,
    overrides,
    spaceParagraphs,
    wrapper = 'div',
    forceInline = false,
  } = VARIANTS[variant] || VARIANTS.body;

  return (
    <Markdown
      className={className}
      options={{
        forceWrapper: true,
        forceInline,
        wrapper,
        overrides,
      }}
    >
      {spaceParagraphs ? children.replace(/\n\n/g, '\n\n &nbsp; \n\n') : children}
    </Markdown>
  );
};

MagicMarkdown.displayName = 'MagicMarkdown';

MagicMarkdown.propTypes = {
  /** Markdown source string. */
  children: string,
  /**
   * `body` for prose, `tile` for emphasis-only text in a tile button, `pill` for the same inside a
   * Stencil `Pill` label.
   */
  variant: oneOf(['body', 'tile', 'pill']),
};
